"use client";

import { useState, useEffect } from "react";
import { ResponsiveDialog } from "@/components/ui/responsive-dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import {
  Zap,
  CheckCircle,
  Tag,
  Sparkles,
  AlertCircle,
  Crown,
  Receipt,
  ChevronRight,
  X as XIcon,
  Loader2,
  LogOut,
} from "lucide-react";
import { useAuth } from "@/components/auth-provider";
import { useUserStore } from "@/store/user-store";
import { toast } from "sonner";
import { api, topUpCredits, previewPromoCode } from "@/lib/api";
import { ymReachGoal } from "@/lib/ym";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Slider } from "@/components/ui/slider";
import NumberFlow from "@number-flow/react";
import { TransactionsModal } from "@/components/transactions-modal";

const PLAN_LABEL: Record<string, string> = {
  starter: "Старт",
  pro: "Про",
  business: "Ультра",
};

const fmtShort = (dateStr: string) => {
  try { return new Date(dateStr).toLocaleDateString("ru-RU", { day: "numeric", month: "short" }); }
  catch { return "—"; }
};
const daysUntil = (dateStr: string) => {
  const diff = new Date(dateStr).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / 86400000));
};

interface AccountModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTab?: string;
  /** Optional banner rendered at the top of the modal. Used when this
   *  modal stands in for the legacy PricingModal — caller passes the
   *  «Нужно X, у вас Y» line so the user sees why we opened it. */
  message?: string;
  /** Render content inline (full page) instead of inside a drawer/dialog
   *  overlay. The dedicated /account route uses this so navigation is a
   *  real route + browser back, not a floating sheet. */
  embedded?: boolean;
}

interface SubPlan {
  id: string;
  name: string;
  price_rub: number;
  credits_per_period: number;
  period_days: number;
}

interface CreditPack {
  id: string;
  credits: number;
  bonus_credits: number;
  price_rub: number;
  badge: string | null;
}

export function AccountModal({ open, onOpenChange, defaultTab, message, embedded }: AccountModalProps) {
  const { user, signOut } = useAuth();
  const [signingOut, setSigningOut] = useState(false);

  const handleSignOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await signOut();
      onOpenChange(false);
    } catch {
      toast.error("Не удалось выйти");
      setSigningOut(false);
    }
  };
  const {
    credits: totalCredits,
    subscriptionCredits,
    purchasedCredits,
    creditPackages,
    plan,
    planPeriodEnd,
    autoRenew,
    isLoading: creditsLoading,
    fetchCredits,
  } = useUserStore();

  const [copied, setCopied] = useState(false);
  const [subPlans, setSubPlans] = useState<SubPlan[]>([]);
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPack, setSelectedPack] = useState<CreditPack | null>(null);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState<any>(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [packsLoading, setPacksLoading] = useState(false);
  const [subPlansLoading, setSubPlansLoading] = useState(false);
  const [showTransactions, setShowTransactions] = useState(false);
  // Paywall mode for non-subscribers: one-time package (default — lowest
  // barrier) vs subscription. Plans shown first scared off one-time buyers
  // (they bounced before scrolling down to packages); a top toggle lets each
  // intent land in its own section. Honors a `defaultTab="subscription"`
  // deep-link and re-syncs on (re)open.
  const [buyMode, setBuyMode] = useState<"package" | "subscription">(
    defaultTab === "subscription" ? "subscription" : "package",
  );
  useEffect(() => {
    if (open) setBuyMode(defaultTab === "subscription" ? "subscription" : "package");
  }, [open, defaultTab]);
  // Active credit packages (ProfileScreen parity — show every pack
  // with progress + days-left, not just the credit total).
  const activePackages = (creditPackages ?? []).filter(p => !p.is_expired);

  const periodStillActive = planPeriodEnd && new Date(planPeriodEnd).getTime() > Date.now();
  // Two ways to land in "cancelled" UX:
  //   (a) explicit /cancel-subscription → plan flipped to "free" but period still ticking
  //   (b) deleted last card → plan kept, but auto_renew flipped off by backend
  const isCancelled = (plan === "free" && Boolean(periodStillActive)) || (plan !== "free" && !autoRenew && Boolean(periodStillActive));
  const hasSubscription = (plan !== "free" && planPeriodEnd) || isCancelled;
  const planLabel = plan === "starter" ? "Старт" : plan === "pro" ? "Про" : plan === "business" ? "Ультра" : "Подписка";
  // When cancelled, infer plan from subscription_credits (exact match or closest >=)
  const currentSubPlan = isCancelled
    ? subPlans.find(p => p.credits_per_period === subscriptionCredits)
      || [...subPlans].sort((a, b) => a.credits_per_period - b.credits_per_period).find(p => p.credits_per_period >= subscriptionCredits)
      || subPlans[0]
    : subPlans.find(p => p.id === plan);

  // ── Paywall layout (non-subscribers / cancelled) ──────────────────────────
  // Show a [Разовый пакет | Подписка] segmented toggle and render ONLY the
  // chosen section, so one-time buyers (the biggest drop-off cohort on web)
  // land on packages without scrolling past a subscription wall. The toggle
  // only appears when BOTH options exist; otherwise the single available one
  // renders with no chooser. Subscribers are unaffected (they just top up).
  const showPaywall = !hasSubscription || isCancelled;
  const plansAvailable = subPlans.length > 0 || subPlansLoading;
  const showToggle = showPaywall && packs.length > 0 && plansAvailable;
  // During the initial load packs aren't in yet, so the toggle can't show.
  // Don't let the plans skeleton flash as the fallback then (it undercuts the
  // package-first default) — only render plans as the sole section once packs
  // have settled and there genuinely are none. packs.length>0 stays a HARD
  // guard on the packs block: its slider derefs sortedPacks[0] and would crash
  // on an empty array.
  const showPlansSection = showPaywall && plansAvailable && (
    buyMode === "subscription" || (!showToggle && !packsLoading)
  );
  const showPacksSection = packs.length > 0 && (!showPaywall || !showToggle || buyMode === "package");
  // "Подписка выгоднее на N%": best ₽/credit across plans vs best across one-off
  // packs. A nudge toward MRR; hidden when subscription isn't actually cheaper.
  const subSavingsPct = (() => {
    if (!subPlans.length || !packs.length) return 0;
    const bestSubRate = Math.min(...subPlans.map(p => p.price_rub / Math.max(p.credits_per_period, 1)));
    const bestPackRate = Math.min(...packs.map(p => p.price_rub / Math.max(p.credits + p.bonus_credits, 1)));
    if (!isFinite(bestSubRate) || !isFinite(bestPackRate) || bestPackRate <= 0) return 0;
    return Math.max(0, Math.round((1 - bestSubRate / bestPackRate) * 100));
  })();

  useEffect(() => {
    if (!open) return;
    setPacksLoading(true);
    setSubPlansLoading(true);
    api.get("/payments/subscription-plans")
      .then(res => {
        const loaded = res.data || [];
        setSubPlans(loaded);
        // Default-select the recommended (last/most expensive) plan so the
        // single bottom CTA has something to act on without an extra tap.
        if (loaded.length > 0) {
          setSelectedPlanId(prev => prev ?? loaded[loaded.length - 1].id);
        }
      })
      .catch((e) => { console.error("Failed to load subscription plans", e); })
      .finally(() => setSubPlansLoading(false));
    api.get("/payments/packages").then(res => {
      const loadedPacks = res.data || [];
      setPacks(loadedPacks);
      if (loadedPacks.length > 0 && !selectedPack) {
        // Select the one with badge or second one
        const preferred = loadedPacks.find((p: CreditPack) => p.badge) || loadedPacks[1] || loadedPacks[0];
        setSelectedPack(preferred);
      }
    }).catch((e) => { console.error("Failed to load credit packages", e); }).finally(() => setPacksLoading(false));
    fetchCredits();
  }, [open, fetchCredits]);

  const handleSubscribe = async (planId: string) => {
    setLoading(true);
    ymReachGoal("topup_started", { method: "card", plan_id: planId, kind: "subscription" });
    try {
      const res = await api.post("/payments/subscribe", { plan_id: planId });
      if (res.data?.payment_url) window.location.href = res.data.payment_url;
    } catch {
      toast.error("Ошибка оформления подписки");
      setLoading(false);
    }
  };

  const handleScrollToPlans = () => {
    // Flip the paywall toggle to subscription first, else the plans block is
    // unmounted and there's nothing to scroll to.
    setBuyMode("subscription");
    setTimeout(() => {
      document.getElementById("account-modal-plans")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  const handleBuyPack = async () => {
    if (!selectedPack) return;
    setLoading(true);
    ymReachGoal("topup_started", { method: "card", package_id: selectedPack.id });
    try {
      await topUpCredits(
        selectedPack.credits + selectedPack.bonus_credits,
        selectedPack.price_rub * 100,
        appliedPromo ? promoCode : undefined,
        selectedPack.id,
      );
    } catch {
      toast.error("Ошибка оплаты");
      setLoading(false);
    }
  };

  const handleApplyPromo = async () => {
    if (!promoCode || !selectedPack) return;
    setPromoLoading(true);
    try {
      const res = await previewPromoCode(promoCode, { type: "purchase", amount: selectedPack.price_rub * 100 });
      if (res.valid) { setAppliedPromo(res); toast.success("Промокод применён"); }
      else { toast.error(res.message || "Недействительный промокод"); }
    } catch { toast.error("Ошибка"); }
    finally { setPromoLoading(false); }
  };

  const subPercent = hasSubscription && currentSubPlan
    ? Math.min(100, (subscriptionCredits / currentSubPlan.credits_per_period) * 100)
    : 0;

  const daysLeft = planPeriodEnd
    ? Math.max(0, Math.ceil((new Date(planPeriodEnd).getTime() - Date.now()) / 86400000))
    : 0;

  const body = (
    <>
        {/* ─── Insufficient-credits banner — only when caller passed a
            `message` (former PricingModal path). Sits above the user
            header so it's the first thing the eye lands on. */}
        {message && (
          <div className="mx-6 mt-5 flex items-start gap-2.5 rounded-xl bg-destructive/10 border border-destructive/20 px-3.5 py-2.5">
            <AlertCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
            <p className="text-[13px] font-medium text-destructive leading-snug">{message}</p>
          </div>
        )}

        {/* ─── Hero: balance + plan badge + breakdown — mirrors miniapp
            ProfileScreen layout for cross-platform consistency. ─── */}
        <div className="px-6 pt-6 pb-4">
          {/* User chip (top) — small avatar + email */}
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-full bg-muted/50 flex items-center justify-center ring-1 ring-border/60 shrink-0 text-[12px] font-bold text-muted-foreground/80">
              {user?.email?.[0]?.toUpperCase() || "?"}
            </div>
            <p className="text-[13px] font-semibold truncate text-foreground/80">{user?.email || "Профиль"}</p>
          </div>

          {/* Hero card */}
          <section className="relative overflow-hidden rounded-2xl border border-border/50 bg-card">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.08] via-transparent to-transparent pointer-events-none" />
            <div className="relative p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-[0.15em]">Баланс</p>
                  {creditsLoading ? (
                    <Skeleton className="h-9 w-32 mt-1.5" />
                  ) : (
                    <p className="mt-1 text-[36px] font-extrabold tracking-tight tabular-nums leading-none">
                      <NumberFlow value={Math.floor(totalCredits || 0)} locales="ru-RU" />
                    </p>
                  )}
                  <p className="text-[10px] text-muted-foreground/70 mt-1">кредитов</p>
                </div>
                <div className="text-right shrink-0">
                  {hasSubscription ? (
                    <>
                      <span className={cn(
                        "inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold",
                        autoRenew && !isCancelled
                          ? "bg-primary/15 text-primary"
                          : "bg-amber-500/15 text-amber-500",
                      )}>
                        <Crown className="w-3 h-3" />
                        {planLabel}
                        {(isCancelled || !autoRenew) && <span className="font-medium opacity-80">· отменена</span>}
                      </span>
                      {planPeriodEnd && (
                        <p className="text-[10px] text-muted-foreground mt-1.5 tabular-nums">
                          {autoRenew && !isCancelled ? "до" : "активна до"} {fmtShort(planPeriodEnd)} · {daysUntil(planPeriodEnd)}д
                        </p>
                      )}
                    </>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full bg-muted/70 text-muted-foreground text-[10px] font-bold">Без подписки</span>
                  )}
                </div>
              </div>
              {Math.floor(subscriptionCredits) > 0 && Math.floor(purchasedCredits) > 0 && (
                <div className="mt-3 pt-3 border-t border-border/30 flex items-center gap-4 text-[11px]">
                  <span className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                    <span className="text-muted-foreground">Подписка</span>
                    <span className="font-semibold tabular-nums">{Math.floor(subscriptionCredits).toLocaleString("ru-RU")}</span>
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    <span className="text-muted-foreground">Пакеты</span>
                    <span className="font-semibold tabular-nums">{Math.floor(purchasedCredits).toLocaleString("ru-RU")}</span>
                  </span>
                </div>
              )}
            </div>
          </section>

          {/* Active packages with progress bars (replaces old per-package
              tile list — denser, miniapp-parity, days-left coloring). */}
          {activePackages.length > 0 && (
            <div className="mt-4 space-y-2">
              <p className="text-[10px] text-muted-foreground/70 tracking-widest uppercase px-0.5">Пакеты · {activePackages.length}</p>
              {activePackages.map(pkg => {
                const days = daysUntil(pkg.expires_at);
                const pct = Math.max(0, Math.min(100, (pkg.remaining / Math.max(pkg.amount, 1)) * 100));
                const isExpiring = days <= 7;
                const isSoonish = days > 7 && days <= 14;
                const barColor = isExpiring ? "bg-red-500" : isSoonish ? "bg-amber-500" : "bg-emerald-500";
                const expiryText = days === 0 ? "истекает сегодня" : days === 1 ? "1 день" : `${days} дней`;
                return (
                  <div key={pkg.id} className="rounded-xl border border-border/40 bg-card p-3 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[13px] font-semibold tabular-nums truncate">
                        {pkg.remaining.toLocaleString("ru-RU")}
                        <span className="text-muted-foreground font-normal"> / {pkg.amount.toLocaleString("ru-RU")} кредитов</span>
                      </p>
                      <p className={cn("text-[10px] font-medium tabular-nums shrink-0", isExpiring ? "text-red-500" : isSoonish ? "text-amber-500" : "text-muted-foreground")}>{expiryText}</p>
                    </div>
                    <div className="h-1 rounded-full bg-muted/60 overflow-hidden">
                      <div className={cn("h-full rounded-full transition-all", barColor)} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ─── Active packages (legacy block — superseded by the hero
            packages list above). Kept off by default to avoid double
            rendering. ─── */}
        {false && !hasSubscription && creditPackages.filter(p => !p.is_expired).length > 0 && (
          <div className="mx-6 mb-5 rounded-2xl border border-white/[0.06] overflow-hidden">
            {(() => {
              const visiblePkgs = creditPackages.filter(p => !p.is_expired);
              let packCounter = 0;
              return visiblePkgs.map((pkg, idx) => {
                const expiresAt = new Date(pkg.expires_at);
                const daysUntil = Math.max(0, Math.ceil((expiresAt.getTime() - Date.now()) / 86400000));
                const remainingPct = Math.max(0, Math.min(100, (pkg.progress ?? (pkg.remaining / Math.max(pkg.amount, 1))) * 100));
                const isWelcome = (pkg.description || "").toLowerCase().includes("welcome");
                const label = isWelcome ? "Welcome bonus" : `Пакет ${++packCounter}`;
                return (
                  <div key={pkg.id} className={cn("px-4 py-3", idx > 0 && "border-t border-white/[0.04]")}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[13px] font-medium">{label}</span>
                      <span className="text-xs tabular-nums">
                        <span className="font-semibold">{Math.floor(pkg.remaining)}</span>
                        <span className="text-muted-foreground"> / {pkg.amount}</span>
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-muted/60 overflow-hidden mt-1.5 mb-1.5">
                      <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${remainingPct}%` }} />
                    </div>
                    <span className={cn("text-[10px]", daysUntil <= 7 ? "text-amber-500/70" : "text-muted-foreground/50")}>
                      до {expiresAt.toLocaleDateString("ru-RU", { day: "numeric", month: "long" })}
                    </span>
                  </div>
                );
              });
            })()}
          </div>
        )}

        {/* ─── Usage breakdown ─── */}
        {/* Detailed subscription panel — kept for managing/cancelling
            since the hero badge above only shows the current state. */}
        {hasSubscription && currentSubPlan && (
          <div className="mx-6 mb-5 rounded-2xl border border-white/[0.06] overflow-hidden">
            {/* Subscription row */}
            <div className="p-4 pb-3">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">{isCancelled ? "Подписка" : planLabel}</span>
                  {isCancelled ? (
                    <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-amber-500/15 text-amber-400">Отменена</span>
                  ) : (
                    <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-emerald-500/15 text-emerald-400">Активна</span>
                  )}
                </div>
                {/* Hide the count + bar for cancelled subs — once leftover
                    subscription_credits get moved into a CreditPackage on
                    cancel/swap the row sits at 0/100 forever and the empty
                    bar is just visual noise. The relevant info is the
                    "Активна до" line + Resume CTA below. */}
                {!isCancelled && (
                  <span className="text-xs tabular-nums">
                    <span className="font-semibold">{Math.floor(subscriptionCredits)}</span>
                    <span className="text-muted-foreground"> / {currentSubPlan.credits_per_period}</span>
                  </span>
                )}
              </div>
              {!isCancelled && (
                <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden mt-2 mb-2">
                  <div
                    className="h-full rounded-full bg-primary transition-all duration-500"
                    style={{ width: `${subPercent}%` }}
                  />
                </div>
              )}
              <div className={cn("flex items-center justify-between", isCancelled && "mt-1")}>
                <span className="text-[10px] text-muted-foreground/60">
                  {isCancelled ? "Активна до" : "Обновление"} {new Date(planPeriodEnd!).toLocaleDateString("ru-RU", { day: "numeric", month: "long" })}
                </span>
                {isCancelled ? (
                  <button
                    onClick={handleScrollToPlans}
                    className="text-[10px] font-semibold text-primary hover:text-primary/80 transition-colors"
                  >
                    Оформить заново
                  </button>
                ) : (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button className="text-[10px] text-muted-foreground/40 hover:text-destructive/60 transition-colors">
                        Отменить
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Отменить подписку?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Авто-продление выключится. Уже начисленные кредиты
                          сохранятся до конца текущего периода
                          {planPeriodEnd ? ` (до ${new Date(planPeriodEnd).toLocaleDateString("ru-RU", { day: "numeric", month: "long" })})` : ""}.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Не отменять</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={async () => {
                            try {
                              await api.post("/payments/cancel-subscription");
                              toast.success("Подписка отменена");
                              await fetchCredits({ force: true });
                            } catch {
                              toast.error("Ошибка отмены");
                            }
                          }}
                        >
                          Отменить подписку
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </div>

            {/* Extra packages — disabled: hero block above already lists
                them with progress bars. */}
            {false && (() => {
              const visiblePkgs = creditPackages.filter(p => !p.is_expired);
              // Count "real" packages (non-welcome) for sequential numbering.
              // Welcome bonus keeps its own label; everything else becomes
              // "Пакет 1", "Пакет 2" — source-specific descriptions
              // ("Подписка Starter (автопродление)", "Перенос с подписки …",
              // YK pack titles) confused users in the cancelled flow.
              let packCounter = 0;
              return visiblePkgs.map(pkg => {
                const expiresAt = new Date(pkg.expires_at);
                const daysUntil = Math.max(0, Math.ceil((expiresAt.getTime() - Date.now()) / 86400000));
                const remainingPct = Math.max(0, Math.min(100, (pkg.progress ?? (pkg.remaining / Math.max(pkg.amount, 1))) * 100));
                const isWelcome = (pkg.description || "").toLowerCase().includes("welcome");
                const label = isWelcome ? "Welcome bonus" : `Пакет ${++packCounter}`;
                return (
                <div key={pkg.id} className="border-t border-white/[0.04] px-4 py-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[13px] font-medium">{label}</span>
                    <span className="text-xs tabular-nums">
                      <span className="font-semibold">{Math.floor(pkg.remaining)}</span>
                      <span className="text-muted-foreground"> / {pkg.amount}</span>
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-muted/60 overflow-hidden mt-1.5 mb-1.5">
                    <div
                      className="h-full rounded-full bg-emerald-500 transition-all"
                      style={{ width: `${remainingPct}%` }}
                    />
                  </div>
                  <span className={cn("text-[10px]", daysUntil <= 7 ? "text-amber-500/70" : "text-muted-foreground/50")}>
                    до {expiresAt.toLocaleDateString("ru-RU", { day: "numeric", month: "long" })}
                  </span>
                </div>
                );
              });
            })()}
          </div>
        )}

        {/* Intent toggle — one-off package vs subscription. Only when both
            exist; lets one-time buyers (the top web drop-off) skip the sub
            wall and land directly on packages. */}
        {showToggle && (
          <div className="px-6 pt-1 pb-3">
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setBuyMode("package")}
                aria-pressed={buyMode === "package"}
                className={cn(
                  "h-12 rounded-xl border flex flex-col items-center justify-center leading-tight transition-all active:scale-[0.99]",
                  buyMode === "package"
                    ? "border-primary bg-primary/10 ring-2 ring-primary/30"
                    : "border-border/60 bg-muted/30 text-muted-foreground hover:text-foreground",
                )}
              >
                <span className="text-[13px] font-bold text-foreground">Разовый пакет</span>
                <span className="text-[9.5px] text-muted-foreground">разовый платёж</span>
              </button>
              <button
                type="button"
                onClick={() => setBuyMode("subscription")}
                aria-pressed={buyMode === "subscription"}
                className={cn(
                  "h-12 rounded-xl border flex flex-col items-center justify-center leading-tight transition-all active:scale-[0.99]",
                  buyMode === "subscription"
                    ? "border-primary bg-primary/10 ring-2 ring-primary/30"
                    : "border-border/60 bg-muted/30 text-muted-foreground hover:text-foreground",
                )}
              >
                <span className="text-[13px] font-bold text-foreground">Подписка</span>
                <span className={cn("text-[9.5px]", subSavingsPct > 0 ? "text-primary font-semibold" : "text-muted-foreground")}>
                  {subSavingsPct > 0 ? `выгоднее на ${subSavingsPct}%` : "каждый месяц"}
                </span>
              </button>
            </div>
          </div>
        )}

        {/* ─── Plans (no subscription, OR cancelled and needs to re-subscribe) ───
            Mirrors the miniapp SubscriptionSheet: full-width selectable plan
            rows + a single bottom CTA, instead of a 3-column squeeze that
            cropped the feature list on phones. */}
        {showPlansSection && (
        <div id="account-modal-plans" className="px-6 pb-5 space-y-3">
          {(!showToggle || isCancelled) && (
            <p className="text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-widest">{isCancelled ? "Оформить подписку заново" : "Планы"}</p>
          )}

          {!subPlansLoading && subPlans.length === 0 ? (
            <div className="rounded-2xl border border-border/40 bg-card/40 px-4 py-6 text-center text-xs text-muted-foreground">
              Тарифы временно недоступны. Попробуйте позже.
            </div>
          ) : subPlansLoading && subPlans.length === 0 ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="rounded-2xl border border-border/40 bg-muted/10 p-4 animate-pulse h-[68px]" />
              ))}
            </div>
          ) : (
            <>
              <div className="space-y-2">
                {subPlans.map((sp, idx) => {
                  const isBest = idx === subPlans.length - 1;
                  const isSelected = selectedPlanId === sp.id;
                  const names = ["Старт", "Про", "Ультра"];
                  return (
                    <button
                      key={sp.id}
                      type="button"
                      onClick={() => setSelectedPlanId(sp.id)}
                      aria-pressed={isSelected}
                      className={cn(
                        "w-full text-left rounded-2xl border p-4 transition-all active:scale-[0.99]",
                        isSelected
                          ? "border-primary bg-primary/10 ring-2 ring-primary/30"
                          : isBest
                            ? "bg-gradient-to-br from-primary/5 via-transparent to-transparent border-primary/20"
                            : "bg-muted/30 border-border/60",
                      )}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-[14px] font-bold text-foreground">{names[idx] ?? sp.name}</p>
                          <p className="text-[11px] text-muted-foreground mt-0.5">{sp.credits_per_period} кредитов/мес</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {isBest && (
                            <span className="px-2 py-0.5 rounded-full bg-primary text-primary-foreground text-[9px] font-bold uppercase tracking-wider">Лучший</span>
                          )}
                          <span
                            aria-hidden
                            className={cn(
                              "w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all",
                              isSelected ? "border-primary bg-primary" : "border-muted-foreground/40",
                            )}
                          >
                            {isSelected && <span className="w-1.5 h-1.5 rounded-full bg-primary-foreground" />}
                          </span>
                        </div>
                      </div>
                      <div className="mt-2 text-[12px]">
                        <span className="tabular-nums font-semibold text-foreground">{sp.price_rub.toLocaleString("ru-RU")} ₽</span>
                        <span className="text-muted-foreground ml-1">/мес</span>
                      </div>
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => selectedPlanId && handleSubscribe(selectedPlanId)}
                disabled={loading || !selectedPlanId}
                className="w-full h-12 rounded-xl bg-primary text-primary-foreground font-bold text-[13px] sm:text-[14px] active:scale-[0.98] disabled:opacity-40 transition-all flex items-center justify-center gap-2 px-3"
              >
                <Sparkles className="w-4 h-4 shrink-0" />
                <span className="truncate">
                  {loading
                    ? "Загрузка..."
                    : selectedPlanId
                      ? `Купить за ${(subPlans.find(p => p.id === selectedPlanId)?.price_rub ?? 0).toLocaleString("ru-RU")} ₽`
                      : "Выберите план"}
                </span>
              </button>
            </>
          )}
        </div>
        )}

        {/* ─── Credit packs — available with or without a subscription.
            Subscribers "top up"; non-subscribers buy a one-off pack. The
            package-vs-subscription choice now lives in the segmented toggle
            above, so the old "или" divider is dropped when it's shown. ─── */}
        {showPacksSection && (
        <div className="px-6 pb-6">
          {!showToggle && (
            <p className="text-[10px] font-semibold text-muted-foreground/50 uppercase tracking-widest mb-3">{hasSubscription ? "Докупить кредиты" : "Купить пакет без подписки"}</p>
          )}

          {/* Slider */}
          {(() => {
            const sortedPacks = [...packs].sort((a, b) => a.credits - b.credits);
            const packIndex = selectedPack ? sortedPacks.findIndex(p => p.id === selectedPack.id) : 0;
            const pack = selectedPack ?? sortedPacks[0];
            const baseTotal = pack.credits + pack.bonus_credits;
            const bonusCredits = appliedPromo?.benefit_type === "bonus_credits" ? Number(appliedPromo.benefit_value) : 0;
            const total = baseTotal + bonusCredits;
            const basePrice = pack.price_rub;
            const finalPrice = appliedPromo?.amount_after != null ? Number(appliedPromo.amount_after) / 100 : basePrice;
            const hasDiscount = appliedPromo && finalPrice !== basePrice;
            return (
              <div className="mb-4">
                <div className="text-center mb-3">
                  <span className="text-2xl font-bold tabular-nums text-primary">{total}</span>
                  <span className="text-xs text-muted-foreground ml-1.5">кредитов</span>
                  <span className="text-xs text-muted-foreground ml-2">за</span>
                  {hasDiscount && (
                    <span className="text-sm tabular-nums ml-1 line-through text-muted-foreground/50">{basePrice} ₽</span>
                  )}
                  <span className={cn("text-sm font-semibold tabular-nums ml-1", hasDiscount && "text-emerald-500")}>{finalPrice} ₽</span>
                </div>
                <Slider
                  min={0}
                  max={sortedPacks.length - 1}
                  step={1}
                  value={[packIndex >= 0 ? packIndex : 0]}
                  onValueChange={([v]) => { setSelectedPack(sortedPacks[v]); setAppliedPromo(null); setPromoCode(""); }}
                  className="mb-2"
                />
                <div className="flex justify-between px-0.5">
                  {sortedPacks.map((pack, i) => {
                    const isActive = packIndex === i;
                    return (
                      <button
                        key={pack.id}
                        onClick={() => { setSelectedPack(pack); setAppliedPromo(null); setPromoCode(""); }}
                        className={cn(
                          "text-[10px] tabular-nums transition-colors",
                          isActive ? "text-primary font-semibold" : "text-muted-foreground/50 hover:text-muted-foreground"
                        )}
                      >
                        {pack.credits + pack.bonus_credits}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Promo */}
          <div className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <Input
                value={promoCode}
                onChange={e => { setPromoCode(e.target.value.toUpperCase()); if (appliedPromo) setAppliedPromo(null); }}
                placeholder="ПРОМОКОД"
                className="uppercase text-xs h-9"
              />
              {appliedPromo && <CheckCircle className="absolute right-3 top-2 h-4 w-4 text-emerald-500" />}
            </div>
            <Button variant="secondary" size="sm" className="h-9 px-3 text-xs shrink-0"
              onClick={appliedPromo ? () => { setAppliedPromo(null); setPromoCode(""); } : handleApplyPromo}
              disabled={!promoCode || promoLoading}
            >
              {appliedPromo ? "×" : "Применить"}
            </Button>
          </div>

          {appliedPromo && (
            <div className="flex items-center gap-2 px-3 py-2 bg-emerald-500/10 rounded-lg text-[11px] text-muted-foreground mb-3">
              <Tag className="w-3 h-3 text-emerald-500 shrink-0" />
              {appliedPromo.message}
            </div>
          )}

          <Button
            onClick={handleBuyPack}
            disabled={loading || !selectedPack}
            className="w-full h-11 font-bold text-sm rounded-xl"
          >
            {loading ? "Оплата..." : selectedPack
              ? (() => {
                  const totalCredits = selectedPack.credits + selectedPack.bonus_credits
                    + (appliedPromo?.benefit_type === "bonus_credits" ? Number(appliedPromo.benefit_value) : 0);
                  const finalPrice = appliedPromo?.amount_after != null
                    ? Number(appliedPromo.amount_after) / 100
                    : selectedPack.price_rub;
                  return `Купить ${totalCredits} кредитов за ${finalPrice} ₽`;
                })()
              : "Выберите пакет"
            }
          </Button>
        </div>
        )}

        {/* Settings — миниап-parity row for «История операций» (and a
            hook for future toggles like «Доставка в чат» on web). Sits
            at the bottom of the modal so the primary CTA above stays
            in the user's eyeline. */}
        <div className="mx-6 mb-6 mt-2 border-t border-border/40 pt-2">
          <p className="text-[10px] text-muted-foreground/70 tracking-widest uppercase px-0.5 mb-1">Настройки</p>
          <button
            onClick={() => setShowTransactions(true)}
            className="w-full flex items-center justify-between py-2.5 active:opacity-60 transition-opacity"
          >
            <div className="flex items-center gap-3 min-w-0">
              <Receipt className="w-4 h-4 text-muted-foreground shrink-0" />
              <p className="text-[13px] text-foreground/80">История операций</p>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground/60" />
          </button>

          <button
            onClick={handleSignOut}
            disabled={signingOut}
            className="w-full flex items-center justify-between py-2.5 active:opacity-60 transition-opacity disabled:opacity-50"
          >
            <div className="flex items-center gap-3 min-w-0">
              {signingOut ? (
                <Loader2 className="w-4 h-4 text-destructive shrink-0 animate-spin" />
              ) : (
                <LogOut className="w-4 h-4 text-destructive shrink-0" />
              )}
              <p className="text-[13px] font-medium text-destructive/90">Выйти из аккаунта</p>
            </div>
          </button>
        </div>

        <TransactionsModal open={showTransactions} onOpenChange={setShowTransactions} />
    </>
  );

  if (embedded) {
    // No horizontal padding here — the DashboardLayout's main wrapper
    // already adds px-3 (mobile) / px-4 (desktop) AND each section in
    // the body has its own mx-6 spacing. Stacking another px-4 made
    // mobile cards visibly cramped.
    return (
      <div className="pb-[calc(6rem+env(safe-area-inset-bottom))] max-w-[640px] mx-auto -mx-3 sm:-mx-4">
        {body}
      </div>
    );
  }

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange} title="Аккаунт" className="sm:max-w-[540px] p-0 gap-0 overflow-hidden border-border/50 sm:max-h-[92vh] sm:overflow-y-auto">
        <DialogHeader className="sr-only">
          <DialogTitle>Аккаунт</DialogTitle>
          <DialogDescription>Управление аккаунтом</DialogDescription>
        </DialogHeader>
        {body}
    </ResponsiveDialog>
  );
}
